import { BrandWordmark } from '@/shared/components';
import { useTranslation } from '@/core/i18n';

interface HeroStat {
  /** i18n key for the small uppercase label (e.g. "Frontdesk"). */
  labelKey: string;
  /** i18n key for the value line under the label. */
  valueKey: string;
}

const HERO_STATS: ReadonlyArray<HeroStat> = [
  { labelKey: 'auth.login.hero.stats.frontdesk.label', valueKey: 'auth.login.hero.stats.frontdesk.value' },
  { labelKey: 'auth.login.hero.stats.commerce.label',  valueKey: 'auth.login.hero.stats.commerce.value' },
  { labelKey: 'auth.login.hero.stats.treasury.label',  valueKey: 'auth.login.hero.stats.treasury.value' },
] as const;

/**
 * LoginHero — left panel of the split-screen login.
 *
 * Coastal background composition (deep-ocean gradient, three blurred color
 * blobs, a slow-pulsing sun disc, an animated waves strip), with brand
 * wordmark at the top, marketing lead + subtitle in the middle, and a
 * three-up product strip at the bottom. All copy is i18n; no fabricated
 * data values appear in the strip.
 */
export function LoginHero() {
  const { t } = useTranslation();

  return (
    <div
      aria-label={t('auth.login.hero.aria')}
      className="relative isolate flex min-h-[420px] flex-col justify-between overflow-hidden p-10 text-sand-50 lg:min-h-screen lg:p-14"
      style={{ backgroundImage: 'linear-gradient(135deg, hsl(var(--ocean-900)) 0%, hsl(var(--ocean-700)) 55%, hsl(var(--ocean-800)) 100%)' }}
    >
      {/* Radial overlays in opposite corners */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: [
            'radial-gradient(circle at 18% 22%, hsl(var(--sun-500) / 0.22), transparent 55%)',
            'radial-gradient(circle at 82% 78%, hsl(var(--coral-500) / 0.22), transparent 55%)',
          ].join(', '),
        }}
      />

      {/* Floating blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-[140px] -top-[120px] h-[420px] w-[420px] rounded-full bg-coral-500/50 blur-[70px] animate-blob-1"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-[80px] -left-[100px] h-[340px] w-[340px] rounded-full bg-sun-500/50 blur-[70px] animate-blob-2"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-[28%] top-[45%] h-[280px] w-[280px] rounded-full bg-sage-500/30 blur-[70px] animate-blob-3"
      />

      {/* Sun disc */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-[18%] top-[18%] h-[110px] w-[110px] rounded-full animate-sun-pulse"
        style={{
          backgroundImage: 'radial-gradient(circle, hsl(var(--sun-100)), hsl(var(--sun-500)))',
          boxShadow: '0 0 80px hsl(var(--sun-500) / 0.35)',
        }}
      />

      {/* Waves strip */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 h-[120px] w-[140%] opacity-55"
        viewBox="0 0 1440 160"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          className="animate-wave-flow"
          d="M0,96 C240,140 480,40 720,80 C960,120 1200,50 1440,90 L1440,160 L0,160 Z"
          fill="hsl(var(--ocean-800) / 0.6)"
        />
        <path
          className="animate-wave-flow-slow"
          d="M0,110 C200,70 520,150 760,110 C1020,70 1220,140 1440,110 L1440,160 L0,160 Z"
          fill="hsl(var(--ocean-900) / 0.55)"
        />
        <path
          className="animate-wave-flow"
          style={{ animationDuration: '16s' }}
          d="M0,130 C260,100 560,160 820,130 C1060,100 1260,150 1440,130 L1440,160 L0,160 Z"
          fill="hsl(var(--sand-900) / 0.35)"
        />
      </svg>

      {/* Top: wordmark + meta line */}
      <div className="relative z-10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* deck.svg sits on a fixed sand-50 tile via arbitrary-value
              background. We avoid the semantic `bg-card` / palette
              `bg-sand-50` tokens here because both get remapped by the
              `.dark` overrides in index.css — that would turn the
              tile dark in dark mode and the navy paths of the SVG
              would vanish into the already-dark hero. */}
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[hsl(36,38%,96%)] p-2 shadow-[0_2px_10px_rgba(0,0,0,0.25)]">
            <img
              src="/deck.svg"
              alt=""
              aria-hidden="true"
              className="h-full w-auto"
              draggable={false}
            />
          </div>
          <BrandWordmark variant="stacked" size="xl" tone="inverse" />
        </div>
        <span className="hidden items-center gap-2 font-mono text-[11px] tracking-wider text-sand-300/90 sm:inline-flex">
          <span
            aria-hidden="true"
            className="inline-flex h-1.5 w-1.5 rounded-full bg-sage-500 animate-sun-pulse"
          />
          {t('auth.login.hero.meta')}
        </span>
      </div>

      {/* Bottom: lead + subtitle + product strip */}
      <div className="relative z-10 max-w-xl">
        <p className="font-serif text-3xl font-medium leading-tight text-sand-50 lg:text-[34px]">
          {t('auth.login.hero.lead')}{' '}
          <em className="font-serif italic font-medium text-sun-300">
            {t('auth.login.hero.leadEm')}
          </em>
        </p>
        <p className="mt-4 max-w-md text-sm leading-relaxed text-sand-200">
          {t('auth.login.hero.subtitle')}
        </p>

        <div className="mt-7 flex flex-wrap gap-7 border-t border-sand-50/15 pt-5">
          {HERO_STATS.map(({ labelKey, valueKey }) => (
            <div key={labelKey} className="flex flex-col gap-1">
              <span className="font-serif text-lg font-semibold leading-none text-sand-50">
                {t(valueKey)}
              </span>
              <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-sand-300">
                {t(labelKey)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default LoginHero;
